import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';

import Box from '@mui/material/Box';

import '../../terminal-locator/Map/Map.css';
import { defaultMapTheme } from '../../terminal-locator/Map/mapDefaults';

const mapContainerStyles = {
  height: { sm: '509px', xs: '231px' },
  maxWidth: 850,
  width: '100%',
};

const Map = ({ centerLatitude, centerLongitude }) => {
  const graphicsLayerRef = useRef();
  const mapContainerRef = useRef();
  const mapViewRef = useRef();
  const webMapRef = useRef();

  // Initialize map
  useEffect(() => {
    webMapRef.current = new WebMap({
      basemap: defaultMapTheme,
    });

    mapViewRef.current = new MapView({
      container: mapContainerRef.current,
      map: webMapRef.current,
      ui: { components: ['attribution'] }, // Remove zoom widget
      zoom: 12,
    });

    // Prevent user from panning and zooming in map
    const stopDragPanningAndZooming = mapViewRef.current.on('drag', event => {
      event.stopPropagation();
    });

    const stopKeydownPanningAndZooming = mapViewRef.current.on('key-down', event => {
      const prohibitedKeys = ['+', '-', 'Shift', '_', '='];

      if (event.key.slice(0, 5) === 'Arrow' || prohibitedKeys.includes(event.key)) {
        event.stopPropagation();
      }
    });

    // Prevent user from zooming in on map
    const stopClickZoom = mapViewRef.current.on('double-click', event => {
      event.stopPropagation();
    });

    const stopMouseZoom = mapViewRef.current.on('mouse-wheel', event => {
      event.stopPropagation();
    });

    graphicsLayerRef.current = new GraphicsLayer();
    webMapRef.current.add(graphicsLayerRef.current);

    // Clean up event listeners
    return () => {
      stopDragPanningAndZooming.remove();
      stopKeydownPanningAndZooming.remove();
      stopClickZoom.remove();
      stopMouseZoom.remove();
    };
  }, []);

  // Center map on chosen location
  useEffect(() => {
    if (mapViewRef.current) {
      mapViewRef.current.center = [centerLongitude, centerLatitude];

      const point = {
        latitude: centerLatitude,
        longitude: centerLongitude,
        type: 'point',
      };

      const pinIcon = {
        height: '53px',
        type: 'picture-marker',
        url: `${process.env.PUBLIC_URL}/icons/map/map-pin-lg.svg`,
        width: '38px',
      };

      const pin = new Graphic({ geometry: point, symbol: pinIcon });

      graphicsLayerRef.current.add(pin);
    }
  }, [centerLatitude, centerLongitude]);

  return <Box ref={mapContainerRef} sx={mapContainerStyles} />;
};

Map.propTypes = {
  centerLatitude: PropTypes.number.isRequired,
  centerLongitude: PropTypes.number.isRequired,
};

export default Map;
